import React, { PureComponent, Fragment } from 'react'
import BEMHelper from '@ynap/bem-helper'
import { Route, Link, withRouter } from 'react-router-dom'
import { Notification } from '@ynap/notification'
import { ChangeEmailForm, PersonalInfoForm, ChangePasswordForm, AccountDetailsSection } from '../index'
import PropTypes from 'prop-types'
import Skeleton from '@ynap/skeleton'
import track from 'react-tracking'
import EmptyList from '@ynap/empty-list'
import { Button } from '@ynap/button'
import { myaccountPageData } from '../../analytics'

const bem = new BEMHelper('AccountDetails')
@track({})
export class AccountDetails extends PureComponent {
    static propTypes = {
        className: PropTypes.string,
        match: PropTypes.shape({
            url: PropTypes.string,
        }),
        messages: PropTypes.shape({
            accountDetails: PropTypes.shape({
                title: PropTypes.func.isRequired,
                personalInfo: PropTypes.func.isRequired,
                email: PropTypes.func.isRequired,
                dob: PropTypes.func.isRequired,
                password: PropTypes.func.isRequired,
                passwordDots: PropTypes.func.isRequired,
                edit: PropTypes.func.isRequired,
                cancel: PropTypes.func.isRequired,
                pageError: PropTypes.func.isRequired,
                retryNow: PropTypes.func.isRequired,
            }),
            titles: PropTypes.objectOf(PropTypes.func).isRequired,
            errors: PropTypes.shape({
                global: PropTypes.func.isRequired,
            }).isRequired,
        }),
        locale: PropTypes.shape({
            id: PropTypes.string.isRequired,
        }).isRequired,
        logonId: PropTypes.string,
        dateOfBirth: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        title: PropTypes.string,
        isImpersonatedBy: PropTypes.string,
        dateOfBirthGroup: PropTypes.object,
        updateUserInfo: PropTypes.func.isRequired,
        updateUserEmail: PropTypes.func.isRequired,
        updateUserPassword: PropTypes.func.isRequired,
        push: PropTypes.func.isRequired,
        updateUserSuccess: PropTypes.string,
        fetchUser: PropTypes.func.isRequired,
        fetchUserError: PropTypes.bool,
        noUppercaseLangs: PropTypes.array,
    }

    updateUserInfo = (newUserInfo) => {
        const { locale, updateUserInfo, push } = this.props
        return updateUserInfo(newUserInfo).then(() => {
            push(`/${locale.id}/account/details`)
        })
    }

    static defaultProps = { title: '' }

    @track(
        myaccountPageData({
            name: 'account details',
            type: 'account details page',
            subCategory1: 'account details',
        })
    )
    componentDidMount() {
        this.props.fetchUser()
    }

    render() {
        const {
            messages,
            className,
            match: routerMatch,
            locale,
            logonId,
            isImpersonatedBy,
            dateOfBirth,
            dateOfBirthGroup,
            firstName,
            lastName,
            title,
            updateUserEmail,
            updateUserPassword,
            updateUserSuccess,
            fetchUser,
            fetchUserError,
            config,
            noUppercaseLangs,
        } = this.props
        const { fullPageHeader } = config?.head?.flags || {}
        const enableAgeConsent = config?.head?.ageConsentCountries?.includes(locale?.country)

        return (
            <div className={bem(null, null, className)}>
                {fetchUserError ? (
                    <EmptyList
                        className={bem('error')}
                        icon={<div className={bem('emptyStateIcon')} />}
                        variant={'myAccountError'}
                        title={messages.accountDetails.pageError()}
                    >
                        <Button onClick={() => fetchUser()} type="primary-link" className={bem('errorLink')}>
                            {messages.accountDetails.retryNow()}
                        </Button>
                    </EmptyList>
                ) : (
                    <Fragment>
                        {!fullPageHeader && <h2 className={bem('title')}>{messages.accountDetails.title()}</h2>}
                        {messages.accountDetails.description && (
                            <p className={bem('description')}>{messages.accountDetails.description()}</p>
                        )}

                        {updateUserSuccess ? (
                            <Notification
                                className={bem('success')}
                                type="success"
                                message={messages.accountDetails.updated({
                                    updateUserSuccess,
                                })}
                            />
                        ) : null}

                        <Route path="/:locale/account/details/update">
                            {({ match }) => (
                                <div className={bem('item')}>
                                    <h3 className={bem('subTitle')}>{messages.accountDetails.personalInfo()}</h3>

                                    {match && firstName ? (
                                        <PersonalInfoForm
                                            messages={messages}
                                            className={bem('form')}
                                            locale={locale}
                                            initialValues={{
                                                personTitle: title,
                                                firstName,
                                                lastName,
                                                dateOfBirthGroup,
                                            }}
                                            onSubmit={this.updateUserInfo}
                                            enableAgeConsent={enableAgeConsent}
                                        >
                                            <Link className={bem('cancel', locale.language)} to={routerMatch.url}>
                                                {messages.accountDetails.cancel()}
                                            </Link>
                                        </PersonalInfoForm>
                                    ) : firstName ? (
                                        <AccountDetailsSection
                                            info={[
                                                <ul className={bem('fullName')} key="fullName">
                                                    <li>
                                                        {messages.accountDetails.fullName({
                                                            title,
                                                            firstName,
                                                            lastName,
                                                        })}
                                                    </li>
                                                </ul>,
                                                dateOfBirth
                                                    ? messages.accountDetails.dob({ dob: dateOfBirth })
                                                    : undefined,
                                            ]}
                                        >
                                            <Link
                                                className={bem('edit')}
                                                to={`/${routerMatch.params.locale}/account/details/update`}
                                            >
                                                {messages.accountDetails.edit()}
                                            </Link>
                                        </AccountDetailsSection>
                                    ) : (
                                        <Skeleton count={2} align="left" className={bem('skeletonText')} />
                                    )}
                                </div>
                            )}
                        </Route>

                        <Route path="/:locale/account/details/email">
                            {({ match }) => (
                                <div className={bem('item')}>
                                    <h3 className={bem('subTitle')}>{messages.accountDetails.email()}</h3>

                                    {match ? (
                                        <ChangeEmailForm
                                            className={bem('form')}
                                            email={logonId}
                                            isImpersonatedBy={isImpersonatedBy}
                                            messages={messages}
                                            onSubmit={updateUserEmail}
                                        >
                                            <Link className={bem('cancel')} to={routerMatch.url}>
                                                {messages.accountDetails.cancel()}
                                            </Link>
                                        </ChangeEmailForm>
                                    ) : logonId ? (
                                        <AccountDetailsSection info={logonId} className={bem('email')}>
                                            <Link
                                                className={bem('edit')}
                                                to={`/${routerMatch.params.locale}/account/details/email`}
                                            >
                                                {messages.accountDetails.edit()}
                                            </Link>
                                        </AccountDetailsSection>
                                    ) : (
                                        <Skeleton className={bem('skeletonText')} />
                                    )}
                                </div>
                            )}
                        </Route>

                        <Route path="/:locale/account/details/password">
                            {({ match }) => (
                                <div className={bem('item')}>
                                    <h3 className={bem('subTitle')}>{messages.accountDetails.password()}</h3>

                                    {match ? (
                                        <ChangePasswordForm
                                            messages={messages}
                                            className={bem('form')}
                                            onSubmit={updateUserPassword}
                                            locale={locale}
                                            noUppercaseLangs={noUppercaseLangs}
                                        >
                                            <Link className={bem('cancel')} to={routerMatch.url}>
                                                {messages.accountDetails.cancel()}
                                            </Link>
                                        </ChangePasswordForm>
                                    ) : logonId ? (
                                        <AccountDetailsSection info={messages.accountDetails.passwordDots()}>
                                            <Link
                                                className={bem('edit')}
                                                to={`/${routerMatch.params.locale}/account/details/password`}
                                            >
                                                {messages.accountDetails.edit()}
                                            </Link>
                                        </AccountDetailsSection>
                                    ) : (
                                        <Skeleton className={bem('skeletonText')} />
                                    )}
                                </div>
                            )}
                        </Route>
                    </Fragment>
                )}
            </div>
        )
    }
}

export const AccountDetailsComponent = AccountDetails
export default withRouter(AccountDetails)
